import React, { useCallback, useEffect, useState, useRef } from 'react'
import { message } from 'antd'
import { useForm } from 'antd/lib/form/Form'
import { useAppContext } from '@/components/AppContext'
import TempleCreation from '../components/stores/TempleCreation'
import StoreCloneModal from '@a4b/ui/src/modules/monetisation/molecules/StoreCloneModal'
import ConfirmationPopup from '@a4b/ui/src/modules/monetisation/molecules/ConfirmationPopup'
import { HubList, LanguageState, TemplesList, STORE_SERVICE_TYPE, SlpFilters } from '@a4b/api/src/modules/Monetisation/Offerings/types'
import { createSearchParams, useSearchParams } from 'react-router-dom'
import { formatCreateTempleApi, formatDate, formatDateStr, parseCreateTempleApiEdit } from '../utils/helper'
import { debounce } from 'lodash'
import { fetchSearchMetadata } from '@/modules/monetisation/search/utils/helper'
import { isCreateRoute } from '@/utils/helper'

interface Props {
  isEditJourney: boolean
  currentStep: number
  storeId: string
  nextHandler: (store_id?: string) => void
  previousHandler: () => void
  languageState: LanguageState
}

const TempleCreationStep: React.FC<Props> = ({
  currentStep,
  isEditJourney,
  storeId,
  previousHandler,
  nextHandler,
  languageState
}) => {
  const { networkInstance, countryState, setLanguageContext, setCountryState } = useAppContext()
  const { monetisationOfferingsApi } = networkInstance.clientWithHeaders
  const [templeFormInstance] = useForm()
  const [hubListData, setHubList] = useState<HubList>()
  const [templesListData, setTemplesList] = useState<TemplesList>()
  const [searchParams, setSearchParams] = useSearchParams()

  const [slugState, setSlugState] = useState<boolean>(false)
  const [storeData, setStoreData] = useState<any>()
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false)
  const [showCloneModal, setShowCloneModal] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isGenieLoading, setIsGenieLoading] = useState<boolean>(false)
  const [locationData, setLocationData] = useState<any>()
  const [addressRemoved, setAddressRemoved] = useState(false);
  const [cloneFormInstance] = useForm()
  const [filterList, setFilterList] = useState<SlpFilters[]>([])

  // Refs to store the genie API promise and pending store data
  const geniePromiseRef = useRef<Promise<any> | null>(null)
  const pendingStoreDataRef = useRef<any>(null)

  const getHubList = useCallback(async () => {
    try {
      const hubList = await monetisationOfferingsApi.hub.getHubList({
        limit: 100,
        offset: 0,
      })
      setHubList(hubList?.data?.data)
    } catch (error) {
      console.log(error)
      message.error('Error while fetching hub list', 3)
    }
  }, [monetisationOfferingsApi.hub])

  const getTemplesList = async (hubId: string) => {
    try {
      const templesList = await monetisationOfferingsApi.templesApi.GetTempleList(hubId, '')
      setTemplesList(templesList?.data?.data)
    } catch (error) {
      console.log(error)
      message.error('Error while fetching temple list', 3)
    }
  }

  const getStore = useCallback(async () => {
    try {
      setIsLoading(true)
      let storeResponse
      if (storeId) {
        storeResponse = await monetisationOfferingsApi.storesApi.getStoreById(
          storeId,
          countryState?.countryCode
        )
      }
      const parsedData = parseCreateTempleApiEdit(storeResponse?.data?.data, languageState)
      setSearchParams(
        createSearchParams({
          // Todo - fix the type later 
          service_type: parsedData?.service_type as any,
          store_type: parsedData?.store_type,
          edit: searchParams.get('edit') || '',
          store_id: searchParams.get("store_id") || "",
          language_code: languageState?.monetisationLanguage,
          active: storeResponse?.data?.data?.active ? "true" : "false",
          country_code: countryState?.countryCode,
          process_date: formatDateStr(parsedData?.process_date) || ''
        }),
      )
      templeFormInstance.setFieldsValue({
        ...parsedData,
        ...(!parsedData?.temple_media?.media_type && {
          temple_media: {
            media_type: 'image'
          }
        })
      })

      setLocationData(parsedData?.location_details)
      setIsLoading(false)
      getTemplesList(parsedData?.hub_id)
    } catch (error) {
      setIsLoading(false)
      console.log(error)
      // message.error('Error while fetching store', 3)
    }
  }, [monetisationOfferingsApi.storesApi, storeId, templeFormInstance, countryState?.countryCode])

  const getFilterTags = useCallback(async () => {
    try {
      const filters = await monetisationOfferingsApi?.slpFilterApi?.getFilters()
      setFilterList(filters?.data?.data)
    } catch (error) {
      message.error({
        content: 'Error while fetching filter List',
        duration: 3,
      })
      console.log(error)
    }
  }, [])

  useEffect(() => {
    if (currentStep === 0) {
      getHubList()
      getFilterTags()
    }
  }, [currentStep, getHubList, getFilterTags])

  useEffect(() => {
    isEditJourney && getStore()
  }, [getStore, isEditJourney])

  useEffect(() => {
    !isEditJourney && templeFormInstance.setFieldsValue({
      temple_media: {
        media_type: 'image'
      }
    })
  }, [countryState?.countryCode, isEditJourney, searchParams, templeFormInstance])

  const handleStoreCreateOrUpdate = async () => {
    // Wait for genie API call to complete if it was initiated
    if (geniePromiseRef.current) {
      try {
        setIsGenieLoading(true)
        await geniePromiseRef.current;
        setIsGenieLoading(false)
      } catch (error) {
        setIsGenieLoading(false)
        console.warn("Genie API call failed, proceeding without search metadata");
      }
    }

    if (isEditJourney) {
      try {
        if (!slugState) {
          const finalStoreData = pendingStoreDataRef.current || storeData
          await monetisationOfferingsApi.storesApi.updateStore(
            finalStoreData,
            storeId
          )
          message.success({ content: 'Temple Updated Successfully', duration: 2 })
          nextHandler()
        } else {
          message.error({ content: 'Please Enter Valid slug', duration: 2 })
        }
      } catch (error: any) {
        setIsGenieLoading(false)
        message.error('Error while updating temple' + error?.message, 3)
      }
    } else {
      try {
        if (!slugState) {
          const finalStoreData = pendingStoreDataRef.current || storeData
          const createResponse = await monetisationOfferingsApi.storesApi.createStore(
            finalStoreData,
          )
          message.success({ content: 'Temple Created Successfully', duration: 2 })
          setSearchParams(
            createSearchParams({
              edit: searchParams.get('edit') || "true",
              store_id: createResponse?.data?.data?.store_id,
              current_step: currentStep.toString(),
              service_type: finalStoreData?.service_type,
              process_date: formatDateStr(finalStoreData?.process_date) || ''
            }),
          )

          setLanguageContext({ ...languageState, isLanguageSelectionActive: false })
          setCountryState({ ...countryState, isCountryCodeActive: false })
          nextHandler(createResponse?.data?.data?.store_id)
        } else {
          message.error({ content: 'Please enter valid slug', duration: 2 })
        }
      } catch (error: any) {
        setIsGenieLoading(false)
        console.log(error)
        message.error('Error while creating temple' + error?.message, 3)
      }
    }
  }

  const handleOk = () => {
    setShowConfirmation(false)
    handleStoreCreateOrUpdate()
  }

  const handleCancel = () => {
    setShowConfirmation(false)
    // Clear the genie promise if user cancels
    geniePromiseRef.current = null
    pendingStoreDataRef.current = null
    setIsGenieLoading(false)
  }

  const handleCloneModal = () => {
    setShowCloneModal((prevState) => (!prevState))
  }

  const updateStoreByDestinationId = async (data: any, values: any) => {
    const storePayload = {
      ...data,
      language_code: values?.destination_language,
      country_code: values?.destination_country
    }
    try {
      await monetisationOfferingsApi.storesApi.updateStore(
        storePayload,
        storeId,
      )
      message.success({ content: `Temple Cloned Successfully to language:${values?.destination_language}, country: ${values?.destination_country} `, duration: 2 })
      setShowCloneModal((prevState) => (!prevState))
    } catch (error: any) {
      message.error('Error while cloning temple' + error?.message, 3)
    }
  }

  const getStoreBySourceId = async (values: any) => {
    const countryCode = values?.source_country
    const languageCode = values?.source_language
    try {
      const storeResponse = await monetisationOfferingsApi.storesApi.getStoreById(
        storeId,
        countryCode,
        languageCode
      )
      const storeData = storeResponse?.data?.data
      if (
        languageCode === storeData?.language_code &&
        countryCode === storeData?.country_code
      ) {
        updateStoreByDestinationId(storeResponse?.data?.data, values)
      } else {
        message.error(`No data available for language: ${languageCode}, country: ${countryCode}`, 3)
      }
    } catch (error) {
      message.error('Error while fetching store by source Id', 3)
    }
  }

  const handleCloneSubmit = () => {
    cloneFormInstance
      .validateFields()
      .then((values: any) => {
        getStoreBySourceId(values)
      })
  }

  // const callGenieApi = async (values: any, isTrueSet: boolean) => {
  //   try {
  //     const searchmetadata = await fetchSearchMetadata(networkInstance);
  //     const payload = {
  //       service_code: 'yoda',
  //       store_data: {
  //         title: templeFormInstance.getFieldValue("title"),
  //         short_description: templeFormInstance.getFieldValue("short_description"),
  //         long_description: templeFormInstance.getFieldValue("long_description"),
  //         display_title: templeFormInstance.getFieldValue("display_title") + templeFormInstance.getFieldValue("sub_title"),
  //       },
  //       festival_enums: searchmetadata?.festivalOptions?.map(option => option?.name) || [],
  //       deity_enums: searchmetadata?.deities?.map(option => option?.value) || [],
  //       tithi_enums: searchmetadata?.thithiOptions?.map(option => option?.name) || []
  //     };

  //     const genieResponse = await networkInstance.clientWithHeaders.monetisationOfferingsApi.systemApi.getSystemGenieData(payload);
  //     const searchResData = {
  //       festival: genieResponse?.data?.data?.data?.model_response_data?.festivals?.length > 0 ?
  //         genieResponse?.data?.data?.data?.model_response_data?.festivals?.[0] : null,
  //       deity: genieResponse?.data?.data?.data?.model_response_data?.deities?.length > 0 ?
  //         genieResponse?.data?.data?.data?.model_response_data?.deities?.[0] : null,
  //       rashi: genieResponse?.data?.data?.data?.model_response_data?.rashis?.length > 0 ?
  //         genieResponse?.data?.data?.data?.model_response_data?.rashis?.[0] : null,
  //       tithi: genieResponse?.data?.data?.data?.model_response_data?.tithis?.length > 0 ?
  //         genieResponse?.data?.data?.data?.model_response_data?.tithis?.[0] : null,
  //     };
  //     if (!isTrueSet) {
  //       values.meta_data.search_metadata.thithi = null
  //       values.meta_data.search_metadata.deity = null
  //       values.meta_data.search_metadata.rashi = null
  //       values.meta_data.search_metadata.festival = null
  //     }

  //     // Update the store data with genie results
  //     let formData;
  //     if (isEditJourney) {
  //       formData = formatCreateTempleApi({
  //         ...values,
  //         position: templeFormInstance.getFieldValue("position"),
  //         id: storeId,
  //       }, languageState, isTrueSet, isEditJourney, countryState?.countryCode, addressRemoved, searchResData);
  //     } else {
  //       formData = formatCreateTempleApi(
  //         values,
  //         languageState,
  //         isTrueSet,
  //         isEditJourney,
  //         countryState?.countryCode,
  //         addressRemoved,
  //         searchResData
  //       );
  //     }

  //     const finalStoreData = { ...formData, country_code: countryState?.countryCode };
  //     pendingStoreDataRef.current = finalStoreData;

  //     return finalStoreData;
  //   } catch (error) {
  //     console.warn(error);
  //     // Return the original store data if genie fails
  //     return pendingStoreDataRef.current;
  //   }
  // }

  const handleTempleCreateOrUpdate = async (values: any) => {
    const isTrueSet = (searchParams.get("active")?.toLowerCase() === 'true');

    // Check if we need to call genie API
    // const shouldCallGenie = (isEditJourney && !isTrueSet) || (
    //   !values?.meta_data?.search_metadata?.thithi &&
    //   !values.meta_data?.search_metadata?.festival &&
    //   !values.metadata?.search_metadata?.rashi &&
    //   !values.metadata?.search_metadata?.deity
    // );

    searchParams.set("service_type", values?.service_type);
    searchParams.set("store_type", values?.store_type);
    searchParams.set("language_code", languageState?.monetisationLanguage);
    searchParams.set("country_code", countryState?.countryCode);
    searchParams.set("process_date", formatDateStr(values?.process_date) || '');
    setSearchParams(searchParams);

    // Prepare initial form data without genie results
    let formData;
    if (isEditJourney) {
      formData = formatCreateTempleApi({
        ...values,
        position: templeFormInstance.getFieldValue("position"),
        id: storeId,
      }, languageState, isTrueSet, isEditJourney, countryState?.countryCode, addressRemoved, null);
    } else {
      formData = formatCreateTempleApi(
        values,
        languageState,
        isTrueSet,
        isEditJourney,
        countryState?.countryCode,
        addressRemoved,
        null
      );
    }

    const initialStoreData = { ...formData, country_code: countryState?.countryCode };
    setStoreData(initialStoreData);
    pendingStoreDataRef.current = initialStoreData;

    // if (shouldCallGenie) {
    //   // Start genie API call but don't wait for it
    //   geniePromiseRef.current = callGenieApi(values, isTrueSet);
    // }

    // Show confirmation modal after 200ms
    // setTimeout(() => {
    setShowConfirmation(true);
    // }, 200);
  };

  const handleTempleCreate = () => {
    templeFormInstance
      .validateFields()
      .then((values: any) => {
        const allValues = templeFormInstance.getFieldsValue(true)
        handleTempleCreateOrUpdate(allValues)
      })
      .catch(error => {
        console.log('Temple Creation error')
      })
  }

  const submitFormHandler = () => {
    handleTempleCreate()
  }

  const validateSlugField = async (slug: string) => {
    if (slug) {
      const slugResponse = await monetisationOfferingsApi.storesApi.getSlugDuplicate(slug)
      setSlugState(slugResponse?.data?.data?.is_duplicate)
      return slugResponse?.data?.data?.is_duplicate
    }
    return
  }

  const debouncedHandler: any = debounce(validateSlugField, 1000)

  const onValuesChange = (values: any) => {
    debouncedHandler(values?.slug)
  }

  const handleRemoveAddress = (removed: any) => {
    setAddressRemoved(removed);
  };

  return (
    <>
      <TempleCreation
        hubList={hubListData}
        form={templeFormInstance}
        submitFormHandler={submitFormHandler}
        isEditJourney={isEditJourney}
        currentStep={currentStep}
        previousHandler={previousHandler}
        nextHandler={() => nextHandler()}
        onValuesChange={onValuesChange}
        slugState={slugState}
        handleCloneModal={handleCloneModal}
        acceptLanguage={languageState?.monetisationLanguage}
        isLoading={isLoading}
        locationDetails={locationData}
        onRemoveAddress={handleRemoveAddress}
        templesList={templesListData}
        filterTags={filterList}
        getTemplesList={getTemplesList}
      />

      {storeData &&
        <ConfirmationPopup
          storeData={storeData}
          visible={showConfirmation}
          handleOk={handleOk}
          handleCancel={handleCancel}
          title={'Store'}
        />
      }
      <StoreCloneModal
        form={cloneFormInstance}
        showCloneModal={showCloneModal}
        handleCloneModal={handleCloneModal}
        handleCloneSubmit={handleCloneSubmit}
      />
    </>
  )
}

export default TempleCreationStep